// ── Cross-quote option clipboard ─────────────────────────────────────────────
//
// Copy an option — or a whole quote's worth of options — out of one quote and
// paste it into another, with both quotes open in separate browser windows.
// It copies the OBJECT: materials with their qty / our_cost / markup_pct /
// pst_pct, the labour hours, the scope, notes and exclusions. Not text.
//
// The source quote is never touched. This is copy, never move.
//
// PURE on purpose — no window, no Date.now, no Math.random — so
// scripts/test_quote_clipboard.js can replay real quote_json blobs through it.
// The caller supplies `nowIso` and a `mintKey` function.
//
// ── WHAT A PASTE MUST FIX, AND WHY ──────────────────────────────────────────
// An option pasted into a DIFFERENT quote is not the same thing it was. Three
// of its fields describe the quote it came FROM, and carrying them across is
// silently wrong. Measured over the last 206 live quotes:
//
//   · labour.rate — 12 distinct rates are in live use (180 … 305). An option
//     lifted out of an old $250/h quote keeps charging $250/h in a $305/h
//     quote unless it is re-rated. This is the exact shape of the incident
//     where 9 quotes reached Jobber carrying two different labour rates.
//   · _permit_fee_override — 92 of those drafts carry one. It is a per-job
//     decision about THAT job's permit; recomputeTotals honours it verbatim,
//     so it would pin the destination's permit to the source job's number.
//   · copied_from — without it quoteMirror's optionMirrorOutMode returns "on"
//     and a foreign client's prices can mirror into options that are already
//     sent. That is Huhn #2656, which repriced two as-sent options by
//     thousands of dollars.
//
// Everything else about the option — the lines, the hours, the wording — is
// the whole point of the copy and is carried across untouched.

export const CLIPBOARD_KEY = "elecbid-option-clipboard";
export const CLIPBOARD_VERSION = 1;
// A custom MIME so a drag between two windows of THIS app is recognisable, and
// text/plain alongside it so the same drag also works where a custom type is
// dropped (and so it can be pasted into a text editor to inspect).
export const DND_MIME = "application/x-elecbid-quote-option";

const clone = (v) => JSON.parse(JSON.stringify(v == null ? null : v));
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const numOr = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);

// ── Building the payload ─────────────────────────────────────────────────────

/**
 * @param quote    the smart_quotes row (for provenance only)
 * @param options  [{ label, recommended, draft }] — already-selected options
 * @param nowIso   caller-supplied timestamp
 */
export function buildPayload({ quote = {}, options = [], nowIso = null } = {}) {
  return {
    v: CLIPBOARD_VERSION,
    kind: "quote_options",
    copied_at: nowIso,
    source: {
      quote_id: quote.id || null,
      quote_number: quote.jobber_quote_number || null,
      client: quote.client_name || null,
      title: quote.project_title || null,
      address: quote.address || null,
      // Recorded so a paste can SAY what rate the lines were priced at before
      // it re-rates them, instead of silently changing the number.
      labour_rate: numOr(options[0]?.draft?.labour?.rate, null),
    },
    options: options.map((o) => ({
      label: o.label || o.draft?.meta?.tier_label || "Option",
      recommended: !!o.recommended,
      draft: clone(o.draft || {}),
    })),
  };
}

export const serializePayload = (payload) => JSON.stringify(payload);

/** Parse + validate. Never throws; returns { ok, payload } or { ok:false, error }. */
export function parsePayload(raw) {
  if (!raw || typeof raw !== "string") return { ok: false, error: "nothing on the clipboard" };
  let p;
  try { p = JSON.parse(raw); } catch { return { ok: false, error: "that isn't an ElecBid option — the clipboard holds something else" }; }
  if (!p || typeof p !== "object") return { ok: false, error: "that isn't an ElecBid option" };
  if (p.kind !== "quote_options") return { ok: false, error: "that isn't an ElecBid option" };
  // A payload written by a newer build may carry fields this one would drop on
  // the floor. Refuse rather than paste a half-understood option.
  if (numOr(p.v, 0) > CLIPBOARD_VERSION) {
    return { ok: false, error: "that option was copied by a newer version of ElecBid — reload this tab and copy it again" };
  }
  if (!Array.isArray(p.options) || !p.options.length) return { ok: false, error: "that clipboard entry has no options in it" };
  if (p.options.some((o) => !o || typeof o !== "object" || !o.draft || typeof o.draft !== "object")) {
    return { ok: false, error: "that clipboard entry is malformed" };
  }
  return { ok: true, payload: p };
}

// ── Preparing an option to land in a different quote ─────────────────────────

/** "Pole feed" → "Pole feed (2)" … against labels already in the destination. */
export function uniqueLabel(label, taken) {
  const base = String(label || "Option").trim() || "Option";
  const lower = new Set([...(taken || [])].map((l) => String(l || "").trim().toLowerCase()));
  if (!lower.has(base.toLowerCase())) return base;
  for (let n = 2; n < 100; n++) {
    const next = `${base} (${n})`;
    if (!lower.has(next.toLowerCase())) return next;
  }
  return `${base} (copy)`;
}

// Lines that ARRIVED in the source option by mirroring carry a mir- id
// (quoteMirror.isMirrorCloneId). In a new quote there is nothing they mirrored
// from, so the marker is a lie — and a lie that makes deleting them local-only.
const demirrorId = (id) => String(id || "").replace(/^(mat|scope|note|exc)_mir_/, "$1_");

/**
 * Strip a draft of everything that describes the quote it came from.
 *
 * @param draft      the source option's draft
 * @param destRate   the DESTINATION quote's labour rate
 * @param suffix     a short unique string for remapped lot ids
 */
export function sanitizeDraftForPaste(draft, { destRate, destPermitMarkupPct, suffix = "p" } = {}) {
  const d = clone(draft) || {};
  const notes = [];

  // ── Quote-level shape must never ride inside an OPTION's draft ────────────
  // Real option drafts have been observed carrying stale `sections` /
  // `is_sectioned`, and a single-option quote's whole body IS its draft, so a
  // copy of one arrives holding `options` and `is_multi_option`. Inside an
  // option those keys are nonsense at best; normalizeLoadedDraft spreads the
  // recommended option's draft onto the quote root, so one promoted to
  // recommended later would start redefining the quote's own shape.
  // include_warranty is not on an option draft by design, but a copy of a
  // SINGLE-option quote's body carries it. normalizeLoadedDraft spreads the
  // recommended option's draft onto the quote root and the push reads
  // draft.include_warranty off the root — so promoting a pasted option later
  // would flip the DESTINATION quote's warranty.
  for (const k of ["options", "is_multi_option", "sections", "is_sectioned", "_section_edit", "_refine", "_save_marker", "include_warranty"]) {
    delete d[k];
  }

  // ── The SOURCE job's review flags are not this job's ──────────────────────
  // They are warnings about the quote it came from — an unverified price there,
  // a permit note there — and they render in the destination's amber/red strip
  // indistinguishable from real ones. The destination re-derives its own on the
  // next save.
  if (Array.isArray(d.review_flags) && d.review_flags.length) d.review_flags = [];

  // ── Materials ──────────────────────────────────────────────────────────────
  // Lot ids are remapped as a PAIR: an anchor's _lot.id and its members'
  // _lot_member must keep pointing at each other, or the group silently breaks
  // apart in the destination (same remap the editor's copy-lines-to-option
  // already does).
  const lotMap = {};
  let downgraded = 0;
  let mats = (d.materials || []).map((m) => {
    const c = { ...m };
    delete c._patched;                       // refers to a refine on the SOURCE quote

    // 🚨 A `supplier_quote` price is a supplier's number for THAT job, with a
    // quote number and an expiry. Carried across it does two wrong things:
    // quote-brain's SUPPLIER_MODE_SKIP exempts the line from the DESTINATION's
    // supplier reprice sweep on the authority of another client's quote, and
    // the provenance tooltip prints that client's supplier quote number and
    // validity as if it were this job's. The COST is the point of the copy and
    // is kept — only the false authority is dropped, to `tech_set`, which the
    // sweep can revisit. `manual_pin` is deliberately left alone: pinning a
    // specific product is a materials decision that does transfer.
    if (c._price_source && c._price_source.tier === "supplier_quote") {
      const ps = c._price_source;
      downgraded++;
      c._price_source = {
        tier: "tech_set",
        confidence: "medium",
        supplier_name: ps.supplier_name || null,
        supplier_sku: ps.supplier_sku || null,
        flag: `carried over from another quote${ps.quote_number ? ` (was ${ps.supplier_name || "supplier"} quote #${ps.quote_number})` : ""} — confirm the price for this job`,
      };
    }
    if (c.id) c.id = demirrorId(c.id);
    if (c._lot_member) c._lot_member = demirrorId(c._lot_member);
    if (c._lot && c._lot.id) {
      const oldId = c._lot.id;
      const newId = `${demirrorId(oldId)}_${suffix}`;
      lotMap[oldId] = newId;
      lotMap[demirrorId(oldId)] = newId;
      c._lot = { ...c._lot, id: newId };
    }
    return c;
  });
  if (downgraded) notes.push(`${downgraded} supplier-quoted price${downgraded === 1 ? "" : "s"} flagged to re-confirm`);
  mats = mats.map((m) => (m._lot_member && lotMap[m._lot_member] ? { ...m, _lot_member: lotMap[m._lot_member] } : m));
  // A member whose anchor did not come along would be permanently hidden.
  const liveLots = new Set(mats.filter((m) => m._lot).map((m) => m._lot.id));
  mats = mats.map((m) => (m._lot_member && !liveLots.has(m._lot_member) ? { ...m, _lot_member: undefined } : m));
  d.materials = mats;

  for (const arr of ["scope", "notes", "exclusions"]) {
    if (Array.isArray(d[arr])) d[arr] = d[arr].map((x) => (x && x.id ? { ...x, id: demirrorId(x.id) } : x));
  }

  // ── Permit ─────────────────────────────────────────────────────────────────
  // A tech override is a decision about the SOURCE job's permit. Dropping it
  // lets recomputeTotals re-derive the fee from this quote's own declared
  // value — which is what the destination job's permit actually costs.
  // Measured over 200 live quotes: 125 drafts carry the key, 92 with a real
  // override and 33 with an explicit null. Delete it whenever it is PRESENT —
  // a truthiness check leaves the null behind — but only report the ones that
  // actually held a number, since dropping a null changes nothing.
  if ("_permit_fee_override" in d) {
    const had = !!d._permit_fee_override;
    delete d._permit_fee_override;
    if (had) notes.push("permit re-derived for this job");
  }
  // recomputeTotals only re-derives a permit LIVE when permit_basis.mode is
  // "value" with brackets. Measured over 321 live option drafts, 189 (59%) are
  // flat/amps/override instead — those keep the number stored on the draft,
  // which is the SOURCE job's permit. Deleting it would be worse (both totals
  // engines fall back to a phantom $811), so it is carried and SAID OUT LOUD.
  if (d.permit_basis?.mode !== "value" && Number(d.permit_fee) > 0) {
    notes.push(`permit $${Number(d.permit_fee)} carried from the source job — check it for this one`);
  }
  // The permit TYPE/amperage the tech chose is about the source job: it drives
  // the permit category dropdown and the amps readout in the destination.
  delete d._permit_basis_override;
  // Permit markup is read per-option, and recomputeTotals only defaults it to
  // 35 when it is null/"" — an explicit 0 wins. So a source option where the
  // tech cleared it pastes 0% into a 35% quote, or the reverse silently adds
  // markup, folded invisibly into the work line.
  const destMk = numOr(destPermitMarkupPct, null);
  if (destMk != null) {
    const wasMk = d.permit_markup_pct;
    if (wasMk != null && wasMk !== "" && Number(wasMk) !== destMk) {
      notes.push(`permit markup ${Number(wasMk)}% → ${destMk}%`);
    }
    d.permit_markup_pct = destMk;
  }

  // ── Labour ─────────────────────────────────────────────────────────────────
  // Hours are the content and are kept exactly. The RATE belongs to the quote,
  // not to the option, so it becomes the destination's.
  const rate = numOr(destRate, null);
  if (d.labour && rate != null) {
    const was = numOr(d.labour.rate, null);
    const hours = numOr(d.labour.total_hours, 0);
    d.labour = { ...d.labour, rate, total: round2(hours * rate) };
    if (was != null && was !== rate) notes.push(`labour re-rated ${was} → ${rate}/h (${hours}h unchanged)`);
  }

  return { draft: d, notes };
}

/**
 * Turn a parsed payload into option objects ready to splice into the
 * destination quote's options array.
 *
 * @param payload  from parsePayload
 * @param dest     { labels, rate, quoteId, isSectioned, mintKey, nowIso }
 * @returns { ok, options, notes, error }
 */
export function prepareOptionsForPaste(payload, dest = {}) {
  // A sectioned quote's phases ADD UP and each pushes as its own firm Jobber
  // line. Adding a pick-one option would silently destroy that shape — the
  // same refusal add_option makes server-side.
  if (dest.isSectioned) {
    return { ok: false, error: "This is a SECTIONED quote — its phases add up rather than being pick-one, so an option can't be pasted in. Change the split in the assessment and re-convert." };
  }
  const mintKey = typeof dest.mintKey === "function" ? dest.mintKey : (i) => `opt_pasted_${i}`;
  const taken = new Set([...(dest.labels || [])]);
  const notes = new Set();
  const sameQuote = dest.quoteId && payload.source?.quote_id && String(dest.quoteId) === String(payload.source.quote_id);

  const options = payload.options.map((o, i) => {
    const label = uniqueLabel(o.label, taken);
    taken.add(label);
    const { draft, notes: n } = sanitizeDraftForPaste(o.draft, {
      destRate: dest.rate,
      destPermitMarkupPct: dest.permitMarkupPct,
      suffix: `${i}${String(mintKey(i)).slice(-4)}`,
    });
    n.forEach((x) => notes.add(x));

    // The SOURCE client's name or address surviving in scope / notes /
    // exclusions is the single most damaging thing this feature can do: that
    // text is what the customer reads and what becomes the Jobber line
    // description. Flagged, NEVER rewritten — a silent find-and-replace mangles
    // legitimate wording worse than the leak it fixes.
    const hits = foreignIdentityHits(draft, { client: payload.source?.client, address: payload.source?.address });
    if (hits.length) {
      draft.review_flags = [
        ...(draft.review_flags || []),
        {
          code: "pasted_foreign_identity",
          severity: "warn",
          message: `Pasted from ${payload.source?.client || "another quote"} — ${hits.length} line${hits.length === 1 ? "" : "s"} still name${hits.length === 1 ? "s" : ""} that job: ${hits.map((h) => `"${h.hit}"`).join(", ")}. Reword before this goes to the client.`,
        },
      ];
      notes.add(`⚠ ${hits.length} line${hits.length === 1 ? "" : "s"} still mention ${payload.source?.client || "the source job"} — reword before sending`);
    }

    draft.meta = {
      ...(draft.meta || {}),
      // REQUIRED: isPlaybook gates the entire editor on it. An option without
      // it renders a full-page "Legacy Quote Format" stub over the builder.
      playbook_version: draft.meta?.playbook_version || "manual@1",
      tier_label: label,
      brain_path: "PASTED_FROM_QUOTE",
      generated_at: dest.nowIso || null,
      pasted_from_quote: payload.source?.quote_number || payload.source?.quote_id || null,
      pasted_from_client: payload.source?.client || null,
      pasted_from_option: o.label || null,
    };

    return {
      key: mintKey(i),
      label,
      // Never inherits recommended: the destination already has one, and
      // exactly one option may carry it.
      recommended: false,
      tier: label,
      // Stamped so quoteMirror treats it as a copy — its edits stay local and
      // it cannot mirror a foreign client's prices into already-priced options.
      copied_from: `quote:${payload.source?.quote_number || payload.source?.quote_id || "?"}:${o.label || "option"}`,
      // optionMirrorOutMode checks mirror_out === true, then === false, then
      // copied_from. An explicit false is the strongest form and costs one key;
      // the COPY_LABEL_RE fallback cannot back copied_from up here, because a
      // pasted label never ends in "(copy)". (Mirror IN is unguarded by
      // design — a guarded option still RECEIVES mirrors.)
      mirror_out: false,
      draft,
    };
  });

  if (sameQuote) notes.add("pasted back into the quote it was copied from");
  return { ok: true, options, notes: [...notes] };
}

/**
 * Client-facing text that still names the job it came from.
 *
 * Matches the client's SURNAME and the street name out of the address (tokens
 * longer than 3 characters, so "Rd" and "St" cannot match everything). Returns
 * where each hit is so the caller can flag it — this never edits the text.
 */
export function foreignIdentityHits(draft, { client, address } = {}) {
  const needles = [];
  const surname = String(client || "").trim().split(/\s+/).filter((w) => w.length > 3).pop();
  if (surname) needles.push(surname);
  for (const tok of String(address || "").split(/[\s,]+/)) {
    // Street name tokens only: skip the number, the province, short suffixes.
    if (tok.length > 3 && !/^\d+$/.test(tok) && !/^(road|street|drive|place|avenue|lane|court|british|columbia|canada)$/i.test(tok)) {
      needles.push(tok);
    }
  }
  if (!needles.length) return [];
  const out = [];
  const scan = (list, text, idx) => {
    const t = String(text || "");
    for (const n of needles) {
      if (new RegExp(`(^|[^a-z0-9])${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`, "i").test(t)) {
        out.push({ list, idx, text: t, hit: n });
        return;
      }
    }
  };
  for (const key of ["scope", "notes", "exclusions"]) {
    (draft?.[key] || []).forEach((x, i) => scan(key, x?.text, i));
  }
  (draft?.labour?.adjustments || []).forEach((a, i) => scan("labour_adjustment", a?.name, i));
  return out;
}

/** One-line description of what is on the clipboard, for the paste button. */
export function describePayload(payload) {
  if (!payload) return "";
  const n = payload.options?.length || 0;
  const who = payload.source?.client || (payload.source?.quote_number ? `#${payload.source.quote_number}` : "another quote");
  if (n === 1) return `${payload.options[0].label} — from ${who}`;
  return `${n} options — from ${who}`;
}
